import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";

const Pricing = () => {
  const [isYearly, setIsYearly] = useState(false);

  return (
    <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Flexible Pricing Plans
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            Choose a plan that fits your needs, with flexible options and transparent pricing.
          </p>
        </div>

        <div className="flex justify-center mb-8">
          <div className="bg-slate-800 rounded-full p-1">
            <button
              onClick={() => setIsYearly(false)}
              className={`px-6 py-2 rounded-full transition-colors ${
                !isYearly ? 'bg-teal-500 text-white' : 'text-slate-400'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsYearly(true)}
              className={`px-6 py-2 rounded-full transition-colors ${
                isYearly ? 'bg-teal-500 text-white' : 'text-slate-400'
              }`}
            >
              Yearly
            </button>
          </div>
        </div>

        <div className="pt-4 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4 lg:gap-8 max-w-6xl mx-auto items-stretch">
          {/* Free Plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            whileHover={{ 
              scale: 1.02,
              y: -5,
              transition: { duration: 0.2 }
            }}
            className="bg-slate-800/50 rounded-2xl p-6 md:p-6 lg:p-8 flex flex-col hover:bg-slate-800/70 hover:shadow-2xl transition-all duration-300 ease-out cursor-pointer"
          >
            <h3 className="text-xl md:text-xl lg:text-2xl font-bold mb-2">Free Plan</h3>
            <p className="text-slate-400 mb-6">Basic access with essential features.</p>
            <div className="mb-8">
              <span className="text-3xl md:text-3xl lg:text-4xl font-bold">$0</span>
              <span className="text-slate-400">/per month</span>
            </div>
            <div className="space-y-4 flex-grow">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-teal-400" />
                <span className="text-slate-300 text-sm md:text-sm lg:text-base">Basic notification batching</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-teal-400" />
                <span className="text-slate-300 text-sm md:text-sm lg:text-base">Up to 3 daily batch times</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-teal-400" />
                <span className="text-slate-300 text-sm md:text-sm lg:text-base">Upt to 15 priority apps</span>
              </div>
              {/* <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-teal-400" />
                <span className="text-slate-300 text-sm md:text-sm lg:text-base">Basic notification insights</span>
              </div> */}
            </div>
            <motion.button 
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.2 }
              }}
              whileTap={{ scale: 0.95 }}
              className="w-full bg-slate-700 text-white py-3 rounded-full font-medium hover:bg-slate-600 hover:shadow-lg transition-all duration-300 ease-out mt-8"
            >
              Get Started
            </motion.button>
          </motion.div>

          {/* Pro Plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            whileHover={{ 
              scale: 1.02,
              y: -5,
              transition: { duration: 0.2 }
            }}
            className="bg-gradient-to-br from-slate-800 to-slate-700 rounded-2xl p-6 md:p-6 lg:p-8 relative border-2 border-teal-400/20 flex flex-col hover:border-teal-400/40 hover:shadow-2xl hover:shadow-teal-500/20 transition-all duration-300 ease-out cursor-pointer"
          >
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-gradient-to-r from-teal-400 to-blue-500 text-white px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap">
                Most Popular
              </span>
            </div>
            <h3 className="text-xl md:text-xl lg:text-2xl font-bold mb-2">Pro Plan</h3>
            <p className="text-slate-400 mb-6">Enhanced features for power users.</p>
            <div className="mb-8">
              {isYearly ? (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-xl md:text-xl lg:text-2xl text-slate-500 line-through">$24</span>
                    <span className="text-3xl md:text-3xl lg:text-4xl font-bold text-teal-400">$20</span>
                    <span className="text-slate-400 text-sm md:text-sm lg:text-base">/per month</span>
                  </div>
                  <div className="flex justify-center">
                    <span className="text-teal-400 text-xs bg-teal-400/10 px-2 py-1 rounded-full">2-Months Free</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-3xl md:text-3xl lg:text-4xl font-bold">$2</span>
                    <span className="text-slate-400 text-sm md:text-sm lg:text-base">/per month</span>
                  </div>
                  <div className="text-center">
                    <span className="text-teal-400 text-xs cursor-pointer hover:underline" onClick={() => setIsYearly(true)}>Save by paying annually</span>
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-4 flex-grow">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-teal-400" />
                <span className="text-slate-300 text-sm md:text-sm lg:text-base">Unlimited batch times</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-teal-400" />
                <span className="text-slate-300 text-sm md:text-sm lg:text-base">Unlimited priority apps</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-teal-400" />
                <span className="text-slate-300 text-sm md:text-sm lg:text-base">Unlimited override rules</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-teal-400" />
                <span className="text-slate-300 text-sm md:text-sm lg:text-base">Priority support</span>
              </div>
              {/* <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-teal-400" />
                <span className="text-slate-300 text-sm md:text-sm lg:text-base">Custom themes</span>
              </div> */}
            </div>
            <motion.button 
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.2 }
              }}
              whileTap={{ scale: 0.95 }}
              className="w-full bg-gradient-to-r from-teal-400 to-blue-500 text-white py-3 rounded-full font-medium hover:opacity-90 hover:shadow-lg hover:shadow-teal-500/30 transition-all duration-300 ease-out mt-8"
            >
              Get Started
            </motion.button>
          </motion.div>

          {/* Enterprise Plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            whileHover={{ 
              scale: 1.02,
              y: -5,
              transition: { duration: 0.2 }
            }}
            className="bg-slate-800/50 rounded-2xl p-6 md:p-6 lg:p-8 flex flex-col hover:bg-slate-800/70 hover:shadow-2xl transition-all duration-300 ease-out cursor-pointer"
          >
            <h3 className="text-xl md:text-xl lg:text-2xl font-bold mb-2">Enterprise Plan</h3>
            <p className="text-slate-400 mb-6">Solutions for teams and organizations.</p>
            <div className="mb-8">
              <div className="flex items-center space-x-3">
                <span className="text-2xl md:text-2xl lg:text-3xl font-bold text-white">Get in Touch</span>
                {/* <span className="text-slate-400">for custom pricing</span> */}
              </div>
            </div>
            <div className="space-y-4 flex-grow">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-teal-400" />
                <span className="text-slate-300 text-sm md:text-sm lg:text-base">Team productivity analytics</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-teal-400" />
                <span className="text-slate-300 text-sm md:text-sm lg:text-base">Employee happiness insights</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-teal-400" />
                <span className="text-slate-300 text-sm md:text-sm lg:text-base">Centralized admin dashboard</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-teal-400" />
                <span className="text-slate-300 text-sm md:text-sm lg:text-base">SSO integration</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-teal-400" />
                <span className="text-slate-300 text-sm md:text-sm lg:text-base">Custom training & onboarding</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-teal-400" />
                <span className="text-slate-300 text-sm md:text-sm lg:text-base">SLA guarantee</span>
              </div>
            </div>
            <motion.button 
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.2 }
              }}
              whileTap={{ scale: 0.95 }}
              className="w-full bg-slate-700 text-white py-3 rounded-full font-medium hover:bg-slate-600 hover:shadow-lg transition-all duration-300 ease-out mt-8"
            >
              Contact Sales
            </motion.button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
